import React from 'react';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';
import { DeveloperProductivity } from '../types';

interface LeaderboardProps {
  developers: DeveloperProductivity[];
  loading?: boolean;
}

function initialsFromName(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

function formatTime(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  
  if (h > 0 && m > 0) {
    return `${h}h ${m}m`;
  } else if (h > 0) {
    return `${h}h`;
  } else {
    return `${m}m`;
  }
}

export function Leaderboard({ developers, loading }: LeaderboardProps) {
  const topHours = developers[0]?.codingHours || 1;

  if (loading) {
    return (
      <section className="panel-card leaderboard-card leaderboard-card--loading">
        <div className="card-header">
          <div>
            <div className="section-kicker">Developer leaderboard</div>
            <h3>Most active contributors in range</h3>
          </div>
        </div>
        <div className="loading-stack">
          <div className="skeleton-line skeleton-line--lg" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
        </div>
      </section>
    );
  }

  return (
    <section className="panel-card leaderboard-card">
      <div className="card-header">
        <div>
          <div className="section-kicker">Developer leaderboard</div>
          <h3>Most active contributors in range</h3>
          <p>Sorted by coding hours and normalized against the top contributor.</p>
        </div>
      </div>

      <div className="leaderboard-list">
        {developers.length === 0 ? (
          <div className="empty-state">No activity yet. Connect the extension to populate the board.</div>
        ) : null}
        {developers.map((developer) => {
          const progress = Math.max(0.08, developer.codingHours / topHours);

          return (
            <article className="leaderboard-row" key={developer.userId}>
              <div className="leaderboard-row__left">
                <div className="avatar avatar--leaderboard">{initialsFromName(developer.name)}</div>
                <div>
                  <div className="leaderboard-name">{developer.name}</div>
                  <div className="role-pill">{developer.role}</div>
                </div>
              </div>

              <div className="leaderboard-row__right">
                <div className="metric-number">{formatTime(developer.codingHours)}</div>
                <div className="progress-bar">
                  <span className="progress-bar__fill" style={{ width: `${progress * 100}%` }} />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mini-history">
        <ResponsiveContainer width="100%" height={72}>
          <BarChart data={developers.slice(0, 6).map((developer) => ({ name: developer.name, hours: developer.codingHours }))}>
            <Bar dataKey="hours" fill="var(--accent-green)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
